import { Link } from "react-router-dom";
import {
  Sparkles, ArrowRight, Building2, Megaphone, Wrench, Receipt,
  FileDown, Video, KeyRound, ScrollText, ShieldCheck, BarChart3, Users2,
} from "lucide-react";

/**
 * SmartRecommendations — personalized "next best action" card for the
 * landlord dashboard, surfaced ABOVE the static feature grid.
 *
 * Reads live portfolio signals (properties, units, occupancy, tickets,
 * applications, expense gap, the calendar month) and ranks features
 * by how much they'll move the operator's needle THIS week.
 *
 * Shown only to subscribed landlords (active or trialing); free-tier
 * landlords already see SmartFeaturesUpsell which sells the full suite.
 *
 * No new backend endpoint — pure derivation from props the Dashboard
 * already fetches. Cheap to render, easy to extend.
 */

// Tax-season window: Feb 1 → Apr 30 (Canadian personal returns due Apr 30,
// rentals via T776 same deadline). Surfaces CRA Filing as top priority.
function isTaxSeason() {
  const m = new Date().getMonth(); // 0=Jan
  return m >= 1 && m <= 3; // Feb, Mar, Apr
}

// Build a ranked list of suggestions from the portfolio snapshot. Each
// suggestion has a priority score; we return the top 3.
function buildSuggestions({ data, tickets, applications, properties }) {
  const totalUnits = data?.units_total || 0;
  const occupied = data?.units_occupied || 0;
  const vacant = Math.max(0, totalUnits - occupied);
  const openTickets = data?.open_tickets || (tickets || []).filter(t => t.status !== "resolved" && t.status !== "closed").length;
  const pendingApps = (applications || []).filter(a => (a.status || "pending") === "pending").length;
  const propCount = properties?.length || data?.properties_count || 0;
  const lastExpenseAt = data?.last_expense_at ? new Date(data.last_expense_at).getTime() : null;
  const daysSinceExpense = lastExpenseAt ? Math.floor((Date.now() - lastExpenseAt) / 86400000) : 999;

  const out = [];

  // 1. Onboarding — no properties yet.
  if (propCount === 0) {
    out.push({
      key: "add-property",
      priority: 100,
      icon: Building2,
      tone: "emerald",
      title: "Add your first property",
      reason: "Your control room comes alive once you've added at least one rental.",
      cta: "Add property",
      to: "/properties?add=1",
    });
  }

  // 2. Vacant units → push Smart Tours + Marketing.
  if (vacant > 0) {
    out.push({
      key: "smart-tours",
      priority: 80 + Math.min(20, vacant * 4),
      icon: Video,
      tone: "sky",
      title: `Fill ${vacant} vacant unit${vacant === 1 ? "" : "s"} with Smart Tours`,
      reason: "Attach a 90-second walkthrough — tenants self-tour, you stay home. Avg fill time drops 38 %.",
      cta: "Set up Smart Tours",
      to: "/smart-tours",
    });
  }

  // 3. Pending applications stacking up → review them.
  if (pendingApps >= 2) {
    out.push({
      key: "applications",
      priority: 75 + Math.min(15, pendingApps * 3),
      icon: Users2,
      tone: "indigo",
      title: `${pendingApps} application${pendingApps === 1 ? "" : "s"} waiting on you`,
      reason: "Smart Mark already ranked them by fit + risk. Most landlords move on offers within 24 h.",
      cta: "Review applications",
      to: "/applications",
    });
  }

  // 4. Open tickets ≥ 2 → Vendors + Smart Locks for vendor PINs.
  if (openTickets >= 2) {
    out.push({
      key: "vendors",
      priority: 70 + Math.min(15, openTickets * 3),
      icon: Wrench,
      tone: "amber",
      title: `Dispatch ${openTickets} open ticket${openTickets === 1 ? "" : "s"}`,
      reason: "Route to a verified Canadian vendor — Smart Locks issues a time-windowed PIN so you don't have to drive over.",
      cta: "Open Vendors",
      to: "/vendors",
    });
  }

  // 5. Tax-season window: CRA Filing T776.
  if (isTaxSeason() && propCount > 0) {
    out.push({
      key: "cra-t776",
      priority: 90,
      icon: FileDown,
      tone: "rose",
      title: "T776 deadline is approaching",
      reason: "Pull your rental income + expenses into a CRA-ready worksheet. Download once, file in minutes.",
      cta: "Prepare T776",
      to: "/cra-filing",
    });
  }

  // 6. No expense logged in 30 days but rent has been collected — likely missed write-offs.
  if (daysSinceExpense > 30 && (data?.rent_collected || 0) > 0) {
    out.push({
      key: "expenses",
      priority: 55,
      icon: Receipt,
      tone: "amber",
      title: "Catch up on expense logging",
      reason: "Every receipt missed is a write-off lost. Snap, tag, file — Smart Mark sorts to the right CRA category.",
      cta: "Log expenses",
      to: "/expenses",
    });
  }

  // 7. No listings but units exist → push Marketing distribution.
  if (propCount > 0 && vacant === 0 && (data?.last_listing_at == null)) {
    out.push({
      key: "marketing",
      priority: 40,
      icon: Megaphone,
      tone: "indigo",
      title: "Build your pipeline before you need it",
      reason: "Auto-distribute listings to Kijiji, Facebook Marketplace, and Realtor.ca — even pre-vacate.",
      cta: "Open Marketing",
      to: "/tours",
    });
  }

  // 8. Mid-occupancy landlord with no announcements yet → engagement.
  if (occupied >= 2 && (data?.announcements_sent ?? 0) === 0) {
    out.push({
      key: "announcements",
      priority: 35,
      icon: ScrollText,
      tone: "sky",
      title: "Send your first tenant announcement",
      reason: "Building updates, parking notices, or rent-due reminders — one composer, all tenants.",
      cta: "Compose announcement",
      to: "/announcements",
    });
  }

  // Always-on fallback so the widget never goes empty. Also used to pad
  // out the shortlist when we only have 1-2 portfolio-driven suggestions.
  const fallbacks = [
    {
      key: "rent-analysis",
      priority: 30,
      icon: BarChart3,
      tone: "emerald",
      title: "Benchmark your rent vs. the market",
      reason: "Most operators leave $80-$220/door/month on the table. See your gap in 30 seconds.",
      cta: "Run Rent Analysis",
      to: "/rent-analysis",
    },
    {
      key: "smart-locks",
      priority: 25,
      icon: KeyRound,
      tone: "amber",
      title: "Issue a time-bounded vendor PIN",
      reason: "Skip key handoffs — Smart Locks expires the PIN automatically.",
      cta: "Open Smart Locks",
      to: "/smart-locks",
    },
    {
      key: "compliance",
      priority: 22,
      icon: ShieldCheck,
      tone: "indigo",
      title: "Run a compliance check",
      reason: "RTA, fire-code, smoke-alarm certifications — surfaced by province.",
      cta: "Open Compliance",
      to: "/compliance",
    },
  ];
  const usedKeys = new Set(out.map(o => o.key));
  for (const f of fallbacks) {
    if (out.length >= 3) break;
    if (!usedKeys.has(f.key)) out.push(f);
  }

  out.sort((a, b) => b.priority - a.priority);
  return out.slice(0, 3);
}

const TONE_BG = {
  emerald: "bg-emerald-100 text-emerald-700",
  sky: "bg-sky-100 text-sky-700",
  amber: "bg-amber-100 text-amber-700",
  rose: "bg-rose-100 text-rose-700",
  indigo: "bg-indigo-100 text-indigo-700",
};

export default function SmartRecommendations({ data, tickets, applications, properties, isSubscribed }) {
  if (!isSubscribed) return null;
  const items = buildSuggestions({ data, tickets, applications, properties });
  if (!items.length) return null;

  return (
    <section
      data-testid="smart-recommendations"
      className="mb-6 rounded-3xl bg-gradient-to-br from-white via-emerald-50/40 to-sky-50/30 border border-emerald-100 shadow-sm overflow-hidden"
    >
      <div className="px-5 sm:px-6 py-5 sm:py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] font-bold tracking-[0.2em] uppercase">
              <Sparkles className="w-3 h-3"/> Smart suggestions
            </span>
            <span className="hidden sm:inline text-[11px] text-slate-500">Personalized to your portfolio</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <Link
                key={it.key}
                to={it.to}
                data-testid={`smart-rec-${it.key}`}
                className="group relative rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-emerald-300 transition-all"
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${TONE_BG[it.tone] || TONE_BG.emerald}`}>
                  <Icon className="w-4.5 h-4.5"/>
                </div>
                <h4 className="font-display text-sm font-bold text-slate-900 mt-3 leading-snug">{it.title}</h4>
                <p className="text-[12px] text-slate-600 mt-1.5 leading-snug">{it.reason}</p>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] font-bold text-emerald-700 group-hover:text-emerald-800">
                  {it.cta} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform"/>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
